import * as angular from 'angular';
import { State } from '../State';
import { OutputViewModel } from 'modules/application/detail/portal-submodule-builder/templates/builder/models/viewModels/OutputViewModel';
import { ContentActionViewModel } from 'modules/application/detail/portal-submodule-builder/templates/builder/models/viewModels/ContentActionViewModel';
import { ActionViewModel } from 'modules/application/detail/portal-submodule-builder/templates/builder/models/viewModels/ActionViewModel';
import { ActionType, } from 'modules/application/detail/portal-submodule-builder/templates/builder/models/Action';
import { Input } from 'modules/application/detail/portal-submodule-builder/templates/builder/models/Input';
import { CardContent } from 'modules/application/detail/portal-submodule-builder/templates/builder/models/viewModels/CardContent';
import { InputViewModel } from 'modules/application/detail/portal-submodule-builder/templates/builder/models/viewModels/InputViewModel';
import { CommonStateViewModel } from './CommonStateViewModel';
import { AgentAction } from '../AgentAction';
import { BuilderAiAgentService } from '../../services/BuilderAiAgentService';
import { AiAgentConstants } from '../../aiAgent/AiAgentConstants';
export class StateViewModel extends CommonStateViewModel {
    static $inject = ['init'];
    constructor(init) {
        super();
        /**
         * Indicates if this is AI generated state
         */
        this.isAiGenerated = false;
        this.$tags = [];
        Object.assign(this, init);
    }
    /**
     * Creates a state from
     * @param state
     */
    static create(state) {
        const stateViewModel = new StateViewModel({
            id: state.id,
            root: state.root,
            end: state.end,
            $title: state.name,
        });
        // Define a function to determine if an action is a content action
        const isContentAction = (action) => action.type == ActionType.SendMessage ||
            action.type == ActionType.SendMessageFromHttp;
        // Define a function to extract content actions from actions
        const getContentActions = (actions) => {
            let contentActions = [];
            actions.forEach((a) => {
                if (isContentAction(a)) {
                    contentActions = contentActions.concat(new ContentActionViewModel({
                        action: new ActionViewModel({
                            ...a,
                            $cardContent: CardContent.create(a.settings),
                        }),
                    }));
                }
            });
            return contentActions;
        };
        // Define a function to extract custom actions from actions
        const getCustomActions = (actions) => {
            let customActions = [];
            actions.forEach((a) => {
                if (!isContentAction(a)) {
                    customActions = customActions.concat(new ActionViewModel({ ...a }));
                }
            });
            return customActions;
        };
        // Add the description field to the AgentAction
        // Define a function to extract custom actions from actions
        const getLocalCustomActions = (actions) => {
            const localCustomActions = [];
            actions.forEach((a) => {
                if (!isContentAction(a)) {
                    localCustomActions.push(new ActionViewModel({
                        $id: a.id,
                        $title: a.title,
                        type: a.type,
                        settings: a.settings,
                        conditions: a.conditions,
                        order: a.order,
                        continueOnError: a.continueOnError,
                        $description: a.description,
                    }));
                }
            });
            return localCustomActions;
        };
        // Add the inputActions
        if (state.inputActions) {
            stateViewModel.$contentActions = stateViewModel.$contentActions.concat(getContentActions(state.inputActions));
            stateViewModel.$enteringCustomActions = stateViewModel.$enteringCustomActions.concat(getCustomActions(state.inputActions));
        }
        // If we do not have an input, create it and set as bypass
        if (!state.input) {
            state.input = new Input();
            state.input.bypass = true;
        }
        const inputContent = state.input.variable != undefined && state.input.variable != ''
            ? state.input.variable
            : undefined;
        // Add the input
        const deletable = state.id == 'onboarding' ? false : true;
        stateViewModel.$contentActions = stateViewModel.$contentActions.concat([
            new ContentActionViewModel({
                input: InputViewModel.create(inputContent, state.input, deletable),
            }),
        ]);
        // Add the outputActions
        if (state.outputActions) {
            stateViewModel.$contentActions = stateViewModel.$contentActions.concat(getContentActions(state.outputActions));
            stateViewModel.$leavingCustomActions = stateViewModel.$leavingCustomActions.concat(getCustomActions(state.outputActions));
        }
        // Add the afterStateChangedActions
        // This property only exists in the desk block
        if (state.afterStateChangedActions && stateViewModel.$afterStateChangedActions) {
            stateViewModel.$afterStateChangedActions = stateViewModel.$afterStateChangedActions.concat(getCustomActions(state.afterStateChangedActions));
        }
        // Add the localCustomActions
        // This property only exists in the AI Agents block
        if (BuilderAiAgentService.isAiAgentState(state.id)) {
            state.localCustomActions = state.localCustomActions ?? [];
            stateViewModel.$localCustomActions = stateViewModel.$localCustomActions ?? [];
            if (state.localCustomActions && stateViewModel.$localCustomActions) {
                stateViewModel.$localCustomActions = stateViewModel.$localCustomActions.concat(getLocalCustomActions(state.localCustomActions));
            }
        }
        if (state.outputs && state.outputs.length) {
            let stateOutputs = [...state.outputs];
            // Check if theres a default output (last output without conditions) defined.
            const stateDefaultOutput = stateOutputs[stateOutputs.length - 1];
            if (stateDefaultOutput.conditions == undefined) {
                stateViewModel.$defaultOutput = new OutputViewModel({
                    ...stateDefaultOutput,
                });
                stateOutputs = stateOutputs.filter((s) => s != stateDefaultOutput);
            }
            stateViewModel.$conditionOutputs = stateOutputs.map((o) => new OutputViewModel({ ...o }));
        }
        return stateViewModel;
    }
    /**
     * Converts the view model instance to an model.
     */
    static createModel(stateViewModel) {
        const state = new State({
            id: stateViewModel.id,
            root: stateViewModel.root,
            end: stateViewModel.end,
            name: stateViewModel.$title,
            shortNameOfSubflow: stateViewModel.shortNameOfSubflow,
            deskStateVersion: stateViewModel.deskStateVersion,
        });
        const copiedStateViewModel = angular.copy(stateViewModel);
        // First, add the entering custom actions
        state.inputActions = stateViewModel.$enteringCustomActions
            ? copiedStateViewModel.$enteringCustomActions
            : [];
        // Update the state values
        const inputIndex = stateViewModel.$contentActions.findIndex((a) => a.input != undefined);
        if (inputIndex >= 0) {
            // Rebuild the state actions.
            // This array contains the state inputActions, input and outputActions mapped in the ContentActionViewModel.
            if (inputIndex > 0) {
                state.inputActions = state.inputActions.concat(copiedStateViewModel.$contentActions
                    .slice(0, inputIndex)
                    .map((a) => a.action));
            }
            state.input = copiedStateViewModel.$contentActions[inputIndex].input;
            state.outputActions =
                inputIndex < stateViewModel.$contentActions.length - 1
                    ? stateViewModel.$contentActions
                        .slice(inputIndex + 1, stateViewModel.$contentActions.length)
                        .map((a) => a.action)
                    : [];
        }
        else {
            state.inputActions = state.inputActions.concat(copiedStateViewModel.$contentActions
                .map((a) => a.action));
            state.input = state.outputActions = undefined;
            state.outputActions = [];
        }
        if (stateViewModel.$leavingCustomActions) {
            state.outputActions = state.outputActions.concat(copiedStateViewModel.$leavingCustomActions);
        }
        state.afterStateChangedActions = [];
        if (stateViewModel.$afterStateChangedActions && state.afterStateChangedActions) {
            state.afterStateChangedActions = state.afterStateChangedActions.concat(copiedStateViewModel.$afterStateChangedActions);
        }
        // Since the types are different, we need to map the values manually
        // with the description field, we need to add here as well
        state.localCustomActions = [];
        if (stateViewModel.$localCustomActions && state.localCustomActions) {
            copiedStateViewModel.$localCustomActions.forEach((a) => {
                state.localCustomActions.push(new AgentAction({
                    id: a.$id,
                    title: a.$title,
                    type: a.type,
                    settings: a.settings,
                    conditions: a.conditions,
                    order: a.order,
                    continueOnError: a.continueOnError,
                    description: a.$description,
                }));
            });
        }
        // Rebuild the state outputs
        let outputs = copiedStateViewModel.$conditionOutputs;
        if (stateViewModel.$defaultOutput) {
            outputs = angular.copy(outputs).concat(stateViewModel.$defaultOutput);
        }
        state.outputs = outputs;
        return state;
    }
    static getAiBlockVersion(state) {
        return state.$enteringCustomActions
            ?.find(action => action.type === ActionType.ForwardToAgent)
            ?.settings?.version ?? AiAgentConstants.CURRENT_AI_AGENTS_VERSION;
    }
}
